import { useState } from 'react';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import { qInvoicesByEncounter, type InvoiceRow } from '@helios/data-access';
import { InvoicesTab } from '../consultation/InvoicesTab';

// ─── Helpers ───

const STATUS_STYLES: Record<string, { bg: string; color: string; label: string }> = {
  draft: { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-text)', label: 'Draft' },
  issued: { bg: 'var(--color-info-bg)', color: 'var(--color-info)', label: 'Issued' },
  paid: { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)', label: 'Paid' },
};

function fmtCurrency(cents: number | null): string {
  if (cents == null) return '—';
  return `₱${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

// ─── Props ───

interface Props {
  patientId: string;
  encounterId: string;
  visitId: string;
  /** Use single-column (dropdown + detail) layout when expanded — for narrow drawers */
  singlePane?: boolean;
}

// ─── Component ───

export function GuidedInvoiceSummary({ patientId, encounterId, visitId, singlePane }: Props) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#78716c';
  const [expanded, setExpanded] = useState(singlePane);

  const { data: invoiceRows = [] } = usePowerSyncQuery<InvoiceRow>(
    qInvoicesByEncounter(patientId, encounterId),
  );

  const invoice = invoiceRows[0] ?? null;

  // If expanded, show full InvoicesTab
  if (expanded) {
    return (
      <div>
        {!singlePane && (
          <div style={{ padding: '0 var(--sp-5)', marginBottom: 'var(--sp-2)' }}>
            <button
              onClick={() => setExpanded(false)}
              style={{
                background: 'none',
                border: 'none',
                color: primary,
                fontSize: 'var(--font-size-xs)',
                fontWeight: 500,
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <NavIcon name="chevron-left" /> Back to summary
            </button>
          </div>
        )}
        <div style={{ padding: singlePane ? 0 : 'var(--sp-3) var(--sp-5)' }}>
          <InvoicesTab
            patientId={patientId}
            encounterId={encounterId}
            visitId={visitId}
            hideHeader
            quietActions
            singlePane={singlePane}
          />
        </div>
      </div>
    );
  }

  // No invoices yet
  if (!invoice) {
    return (
      <div
        style={{
          padding: 'var(--sp-5) var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)',
            fontStyle: 'italic',
          }}
        >
          No invoice for this visit
        </span>
        <button
          onClick={() => setExpanded(true)}
          style={{
            height: 28,
            padding: '0 10px',
            borderRadius: 'var(--radius-sm)',
            border: `1px solid ${primary}`,
            background: 'transparent',
            color: primary,
            fontSize: 'var(--font-size-xs)',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <NavIcon name="plus" /> Create Invoice
        </button>
      </div>
    );
  }

  // Compact summary
  const status = STATUS_STYLES[invoice.status ?? ''] ?? STATUS_STYLES.draft;

  return (
    <div style={{ padding: 'var(--sp-4) var(--sp-6)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-4)',
          flexWrap: 'wrap',
        }}
      >
        {/* Invoice number + status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--text)' }}>
            {invoice.invoice_no ?? 'INV'}
          </span>
          <span
            style={{
              display: 'inline-block',
              padding: '1px 8px',
              borderRadius: 'var(--radius-full)',
              background: status.bg,
              color: status.color,
              fontSize: 'var(--font-size-xs)',
              fontWeight: 500,
            }}
          >
            {status.label}
          </span>
        </div>

        {/* Total */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>Total</span>
          <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--text)' }}>
            {fmtCurrency(invoice.grand_total)}
          </span>
        </div>

        <div style={{ flex: 1 }} />

        {/* Open full editor */}
        <button
          onClick={() => setExpanded(true)}
          style={{
            height: 28,
            padding: '0 10px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          Open Invoice
        </button>
      </div>
    </div>
  );
}
